// Shrinks photos before upload and drops their metadata (EXIF, including GPS
// location) by re-encoding them in the browser.

import { ApiError } from "./api";

/** Long-edge cap: 4K screens gain nothing from larger originals. */
export const MAX_IMAGE_EDGE = 3840;

const QUALITY: Record<string, number | undefined> = {
  "image/jpeg": 0.88,
  "image/webp": 0.9,
  "image/png": undefined, // lossless; keeps transparency
};

export const HEIC_MESSAGE =
  "iPhone HEIC photos can't be shown on the screens. Export it as a JPEG first, or set iPhone Camera > Formats > Most Compatible.";

/** Scale (width, height) down so neither side exceeds `max`, keeping the aspect
 *  ratio. Never upscales. */
export function fitWithin(width: number, height: number, max: number): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (!(longEdge > max) || !(max > 0)) return { width, height };
  const scale = max / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Browsers and OSes report a few aliases for the same formats. */
const TYPE_ALIASES: Record<string, string> = {
  "image/jpg": "image/jpeg",
  "image/pjpeg": "image/jpeg",
  "image/x-png": "image/png",
};

function isHeic(file: File): boolean {
  const type = file.type.toLowerCase();
  return type === "image/heic" || type === "image/heif" || /\.hei[cf]$/i.test(file.name);
}

/** Animated WebP/PNG would be flattened to their first frame by a canvas. */
async function isAnimated(file: File): Promise<boolean> {
  const head = new Uint8Array(await file.slice(0, 64 * 1024).arrayBuffer());
  // One char per byte, so string offsets are byte offsets.
  const text = new TextDecoder("latin1").decode(head);
  if (file.type === "image/webp") {
    // VP8X extended header, animation flag.
    return text.slice(12, 16) === "VP8X" && (head[20] & 0x02) !== 0;
  }
  if (file.type === "image/png") {
    // APNG declares an acTL chunk before the first IDAT.
    const actl = text.indexOf("acTL");
    const idat = text.indexOf("IDAT");
    return actl !== -1 && (idat === -1 || actl < idat);
  }
  return false;
}

/**
 * Prepare a picture for upload: JPEG/PNG/WebP are resized to fit within
 * MAX_IMAGE_EDGE and always re-encoded (which strips EXIF/GPS) in the same
 * format. Other types pass through untouched; HEIC is rejected because the
 * screens can't display it. If the browser can't decode/encode, the original
 * is uploaded with its location metadata scrubbed (see stripLocationBytes).
 */
export async function prepareImageForUpload(file: File): Promise<File> {
  if (isHeic(file)) throw new ApiError(415, HEIC_MESSAGE);
  const raw = file.type.toLowerCase();
  const type = TYPE_ALIASES[raw] ?? raw;
  if (!(type in QUALITY)) return file;
  // Whenever re-encoding isn't possible, still remove location data from the
  // original bytes — uploads are served publicly.
  const fallback = () => stripLocationMetadata(file, type);
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") return fallback();

  let bitmap: ImageBitmap;
  try {
    if (await isAnimated(file)) return fallback();
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return fallback();
  }
  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height, MAX_IMAGE_EDGE);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return fallback();
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, QUALITY[type]));
    // Some browsers silently fall back to PNG for formats they can't encode.
    if (!blob || blob.type !== type) return fallback();
    return new File([blob], file.name, { type, lastModified: file.lastModified });
  } catch {
    return fallback();
  } finally {
    bitmap.close();
  }
}

async function stripLocationMetadata(file: File, type: string): Promise<File> {
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const cleaned = stripLocationBytes(bytes, type);
    if (!cleaned) return file;
    return new File([new Uint8Array(cleaned)], file.name, { type, lastModified: file.lastModified });
  } catch {
    return file;
  }
}

/**
 * Byte-level metadata scrub used when the browser can't re-encode a picture.
 * JPEG: GPS fields inside EXIF are zeroed in place (keeping Orientation, so
 * phone photos still display upright) and XMP / IPTC blocks are dropped.
 * PNG: eXIf and text chunks are dropped. WebP: EXIF and XMP chunks are
 * dropped. Returns null if the file isn't laid out as expected.
 */
export function stripLocationBytes(bytes: Uint8Array, type: string): Uint8Array | null {
  if (type === "image/jpeg") return stripJpeg(bytes);
  if (type === "image/png") return stripPng(bytes);
  if (type === "image/webp") return stripWebp(bytes);
  return null;
}

const ascii = (b: Uint8Array, start: number, len: number) =>
  String.fromCharCode(...b.subarray(start, start + len));

function stripJpeg(b: Uint8Array): Uint8Array | null {
  if (b.length < 4 || b[0] !== 0xff || b[1] !== 0xd8) return null;
  const parts: Uint8Array[] = [b.subarray(0, 2)];
  let pos = 2;
  while (pos < b.length) {
    if (b[pos] !== 0xff) return null;
    const marker = b[pos + 1];
    if (marker === 0xff) {
      pos++; // fill byte
      continue;
    }
    if (marker === 0xda) {
      parts.push(b.subarray(pos)); // start of scan: image data to the end
      break;
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      parts.push(b.subarray(pos, pos + 2));
      pos += 2;
      continue;
    }
    if (pos + 4 > b.length) return null;
    const end = pos + 2 + ((b[pos + 2] << 8) | b[pos + 3]);
    if (end > b.length) return null;
    const segment = b.slice(pos, end);
    const isExif = marker === 0xe1 && ascii(segment, 4, 6) === "Exif\0\0";
    if (isExif) {
      scrubExifGps(segment, 10);
      parts.push(segment);
    } else if (marker !== 0xe1 && marker !== 0xed) {
      parts.push(segment); // keep everything but XMP (APP1) and IPTC (APP13)
    }
    pos = end;
  }
  return concat(parts);
}

/** Zero the GPS IFD (entries and the values they point at) inside a TIFF
 *  block starting at `tiff`, leaving all other tags untouched. */
function scrubExifGps(seg: Uint8Array, tiff: number): void {
  const le = ascii(seg, tiff, 2) === "II";
  const u16 = (o: number) => (le ? seg[o] | (seg[o + 1] << 8) : (seg[o] << 8) | seg[o + 1]);
  const u32 = (o: number) =>
    (le
      ? seg[o] | (seg[o + 1] << 8) | (seg[o + 2] << 16) | (seg[o + 3] << 24)
      : (seg[o] << 24) | (seg[o + 1] << 16) | (seg[o + 2] << 8) | seg[o + 3]) >>> 0;
  const inRange = (o: number, n: number) => o >= 0 && o + n <= seg.length;
  const ifd0 = tiff + u32(tiff + 4);
  if (!inRange(ifd0, 2)) return;
  const count = u16(ifd0);
  for (let i = 0; i < count; i++) {
    const entry = ifd0 + 2 + i * 12;
    if (!inRange(entry, 12) || u16(entry) !== 0x8825) continue; // GPSInfo pointer
    const gps = tiff + u32(entry + 8);
    if (!inRange(gps, 2)) return;
    const n = u16(gps);
    const SIZE: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1, 9: 4, 10: 8 };
    for (let j = 0; j < n; j++) {
      const e = gps + 2 + j * 12;
      if (!inRange(e, 12)) break;
      const bytes = (SIZE[u16(e + 2)] ?? 1) * u32(e + 4);
      if (bytes > 4) {
        const at = tiff + u32(e + 8);
        if (inRange(at, bytes)) seg.fill(0, at, at + bytes);
      }
      seg.fill(0, e, e + 12);
    }
    seg.fill(0, gps, gps + 2); // the GPS IFD now has zero entries
    return;
  }
}

function stripPng(b: Uint8Array): Uint8Array | null {
  if (b.length < 8 || ascii(b, 1, 3) !== "PNG") return null;
  const DROP = new Set(["eXIf", "tEXt", "zTXt", "iTXt"]);
  const parts: Uint8Array[] = [b.subarray(0, 8)];
  let pos = 8;
  while (pos + 12 <= b.length) {
    const len = ((b[pos] << 24) | (b[pos + 1] << 16) | (b[pos + 2] << 8) | b[pos + 3]) >>> 0;
    const end = pos + 12 + len;
    if (end > b.length) return null;
    if (!DROP.has(ascii(b, pos + 4, 4))) parts.push(b.subarray(pos, end));
    pos = end;
  }
  return pos === b.length ? concat(parts) : null;
}

function stripWebp(b: Uint8Array): Uint8Array | null {
  if (b.length < 12 || ascii(b, 0, 4) !== "RIFF" || ascii(b, 8, 4) !== "WEBP") return null;
  const parts: Uint8Array[] = [];
  let pos = 12;
  while (pos + 8 <= b.length) {
    const fourcc = ascii(b, pos, 4);
    const len = (b[pos + 4] | (b[pos + 5] << 8) | (b[pos + 6] << 16) | (b[pos + 7] << 24)) >>> 0;
    const end = Math.min(b.length, pos + 8 + len + (len % 2));
    if (pos + 8 + len > b.length) return null;
    if (fourcc === "VP8X") {
      const chunk = b.slice(pos, end);
      chunk[8] &= ~(0x08 | 0x04); // clear the EXIF and XMP flags
      parts.push(chunk);
    } else if (fourcc !== "EXIF" && fourcc !== "XMP ") {
      parts.push(b.subarray(pos, end));
    }
    pos = end;
  }
  const body = concat(parts);
  const out = new Uint8Array(12 + body.length);
  out.set(b.subarray(0, 12));
  out.set(body, 12);
  const size = out.length - 8;
  out[4] = size & 0xff;
  out[5] = (size >> 8) & 0xff;
  out[6] = (size >> 16) & 0xff;
  out[7] = (size >>> 24) & 0xff;
  return out;
}

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}
